<template>
  <v-container fluid class="pa-6">
    <!-- Breadcrumbs -->
    <div class="spv-breadcrumbs-container">
      <Breadcrumbs />
    </div>

    <!-- Hero Header Card -->
    <v-card class="spv-hero-card mb-6" elevation="6">
      <div class="spv-hero-header">
        <div class="d-flex align-center">
          <v-avatar size="48" class="spv-hero-logo mr-4">
            <v-icon dark size="26">mdi-handshake-outline</v-icon>
          </v-avatar>
          <div>
            <h1 class="spv-hero-title">IACS Service Provider Characterization</h1>
            <p class="spv-hero-subtitle mb-0">
              Define the service provider type and scope for your certification
            </p>
          </div>
        </div>
      </div>

      <!-- Step Progress -->
      <div class="spv-steps-bar">
        <div class="spv-step spv-step-done">
          <div class="spv-step-circle spv-step-circle-done">
            <v-icon size="18" color="white">mdi-check</v-icon>
          </div>
          <span class="spv-step-label">Define a Subject of Certification</span>
        </div>
        <div class="spv-step-line spv-step-line-done"></div>
        <div class="spv-step spv-step-active">
          <div class="spv-step-circle spv-step-circle-active">2</div>
          <span class="spv-step-label">Configure</span>
        </div>
        <div class="spv-step-line"></div>
        <div class="spv-step">
          <div class="spv-step-circle">3</div>
          <span class="spv-step-label">Review</span>
        </div>
      </div>
    </v-card>

    <!-- Form Card -->
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <v-card class="spv-form-card" elevation="4">
          <v-form ref="serverForm" v-model="valid" lazy-validation>
            <v-card-text class="pa-6">
              <!-- Service Provider Type -->
              <div class="spv-field-group mb-5">
                <div class="spv-field-label">
                  <v-icon small color="#1976d2" class="mr-1">mdi-domain</v-icon>
                  Service Provider Type
                </div>
                <v-radio-group v-model="serviceProviderType" class="mt-2">
                  <div class="spv-radio-card mb-2">
                    <v-radio value="Integration Services" color="primary">
                      <template v-slot:label>
                        <div class="d-flex align-center" style="gap: 10px;">
                          <div class="spv-radio-icon spv-radio-icon--blue">
                            <v-icon size="18" color="white">mdi-puzzle-outline</v-icon>
                          </div>
                          <div>
                            <div class="spv-radio-title">Integration Services</div>
                            <div class="spv-radio-desc">
                              System integration and deployment services
                            </div>
                          </div>
                        </div>
                      </template>
                    </v-radio>
                  </div>
                  <div class="spv-radio-card mb-2">
                    <v-radio value="Maintenance Services" color="primary">
                      <template v-slot:label>
                        <div class="d-flex align-center" style="gap: 10px;">
                          <div class="spv-radio-icon spv-radio-icon--purple">
                            <v-icon size="18" color="white">mdi-wrench-outline</v-icon>
                          </div>
                          <div>
                            <div class="spv-radio-title">Maintenance Services</div>
                            <div class="spv-radio-desc">Ongoing system maintenance and support</div>
                          </div>
                        </div>
                      </template>
                    </v-radio>
                  </div>
                  <div class="spv-radio-card">
                    <v-radio value="Both" color="primary">
                      <template v-slot:label>
                        <div class="d-flex align-center" style="gap: 10px;">
                          <div class="spv-radio-icon spv-radio-icon--teal">
                            <v-icon size="18" color="white">mdi-check-all</v-icon>
                          </div>
                          <div>
                            <div class="spv-radio-title">Both</div>
                            <div class="spv-radio-desc">Integration and maintenance combined</div>
                          </div>
                        </div>
                      </template>
                    </v-radio>
                  </div>
                </v-radio-group>
              </div>

              <!-- Service Scope -->
              <div class="spv-field-group">
                <div class="spv-field-label">
                  <v-icon small color="#0097a7" class="mr-1">mdi-clipboard-text-outline</v-icon>
                  Service Scope
                </div>
                <v-text-field
                  v-model="activitiesOrSlas"
                  label="Describe specific activities or SLAs"
                  outlined
                  dense
                />
              </div>
            </v-card-text>

            <!-- Action Buttons -->
            <v-divider></v-divider>
            <v-card-actions class="pa-5 d-flex justify-center">
              <v-btn class="spv-btn-clear mr-3" @click="clearForm">
                <v-icon left small>mdi-eraser</v-icon>
                Clear
              </v-btn>
              <v-btn class="spv-btn-back mr-3" @click="$router.go(-1)">
                <v-icon left small>mdi-arrow-left</v-icon>
                Back
              </v-btn>
              <v-btn class="spv-btn-next" :loading="loading" @click="handleNext">
                Next
                <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import util from '@/mixins/util';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';

export default {
  name: 'ServiceProviderView',
  components: {
    Breadcrumbs,
  },
  mixins: [util],
  data() {
    return {
      loading: false,
      valid: false,
      serviceProviderType: '',
      activitiesOrSlas: '',
      certificationName: this.$route.query.name || '',
      certificationDescription: this.$route.query.description || '',
      subjectOfCertification: this.$route.params.subject || '',
      family: this.$route.query.family || '',
      origin: this.$route.query.origin || 'manual',
      threatSpiderComponent: null,
      threatSpiderStandard: null,
    };
  },
  created() {
    // Get component and standard from query params
    const threatSpiderComponent = this.$route.query.threatSpiderComponent;
    const threatSpiderStandard = this.$route.query.threatSpiderStandard;

    if (threatSpiderComponent && threatSpiderStandard) {
      this.threatSpiderComponent = threatSpiderComponent;
      this.threatSpiderStandard = threatSpiderStandard;

      // Set service provider type based on component info
      if (this.threatSpiderComponent.ServiceType) {
        this.serviceProviderType = this.threatSpiderComponent.ServiceType;
      }

      // Set activities/SLAs from component info
      if (this.threatSpiderComponent.ServiceScope) {
        this.activitiesOrSlas = this.threatSpiderComponent.ServiceScope;
      }
    }
  },
  mounted() {
    console.log('Certification Name:', this.certificationName);
    console.log('Certification Description:', this.certificationDescription);
    console.log('Subject of Certification:', this.subjectOfCertification);
    console.log('Family:', this.$route.query.family);
  },
  methods: {
    handleNext() {
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
        this.$router.push({
          name: 'Requirements',
          params: {
            subject: this.subject || this.$route.params.subject,
          },
          query: {
            name: this.certificationName,
            description: this.certificationDescription,
            family: this.family,
            serviceProviderType: this.serviceProviderType,
            activitiesOrSlas: this.activitiesOrSlas,
            origin: this.origin,
            threatSpiderStandard: this.threatSpiderStandard,
          },
        });
      }, 1000);
    },
    clearForm() {
      this.serviceProviderType = '';
      this.activitiesOrSlas = '';
      this.valid = false;
    },
  },
};
</script>

<style scoped>
/* ============================================
   SERVICE PROVIDER VIEW - Modern Design
   ============================================ */

.spv-breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 28px;
}

.spv-breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* Hero Card */
.spv-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
  background: #ffffff;
}

.spv-hero-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 24px 28px;
}

.spv-hero-logo {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
}

.spv-hero-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.spv-hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.92rem;
  font-weight: 400;
}

/* Step Progress Bar */
.spv-steps-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 28px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-top: 1px solid #e8eef3;
}

.spv-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.spv-step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  background: #e0e0e0;
  color: #9e9e9e;
  transition: all 0.3s ease;
}

.spv-step-circle-active {
  background: linear-gradient(135deg, #00405e, #0097a7);
  color: white;
  box-shadow: 0 4px 14px rgba(0, 100, 148, 0.4);
}

.spv-step-circle-done {
  background: linear-gradient(135deg, #388e3c, #66bb6a);
  color: white;
  box-shadow: 0 4px 14px rgba(56, 142, 60, 0.35);
}

.spv-step-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #78909c;
}

.spv-step-active .spv-step-label {
  color: #00405e;
}

.spv-step-done .spv-step-label {
  color: #388e3c;
}

.spv-step-line {
  flex: 1;
  max-width: 80px;
  height: 3px;
  background: linear-gradient(90deg, #e0e0e0, #bdbdbd);
  border-radius: 2px;
  margin: 0 12px;
  margin-bottom: 22px;
}

.spv-step-line-done {
  background: linear-gradient(90deg, #66bb6a, #a5d6a7);
}

/* Form Card */
.spv-form-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
}

.spv-field-group {
  position: relative;
}

.spv-field-label {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: #455a64;
  margin-bottom: 6px;
  letter-spacing: 0.3px;
}

/* Radio Cards */
.spv-radio-card {
  padding: 12px 16px;
  background: linear-gradient(135deg, #f5f7fa 0%, #fafbfc 100%);
  border: 1px solid #e8ecf1;
  border-radius: 12px;
  transition: all 0.25s ease;
}

.spv-radio-card:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 50%, #e8f5e9 100%);
  border-color: #bbdefb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.spv-radio-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.spv-radio-icon--blue {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
}

.spv-radio-icon--purple {
  background: linear-gradient(135deg, #7b1fa2, #ab47bc);
}

.spv-radio-icon--teal {
  background: linear-gradient(135deg, #00695c, #26a69a);
}

.spv-radio-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #37474f;
}

.spv-radio-desc {
  font-size: 0.78rem;
  color: #90a4ae;
  margin-top: 1px;
}

/* Action Buttons */
.spv-btn-clear {
  background: linear-gradient(135deg, #f37d21 0%, #ff9800 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 20px !important;
  box-shadow: 0 4px 12px rgba(243, 125, 33, 0.3) !important;
  transition: all 0.3s ease !important;
}
.spv-btn-clear:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(243, 125, 33, 0.5) !important;
}

.spv-btn-back {
  background: linear-gradient(135deg, #78909c 0%, #90a4ae 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 20px !important;
  box-shadow: 0 4px 12px rgba(120, 144, 156, 0.3) !important;
  transition: all 0.3s ease !important;
}
.spv-btn-back:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(120, 144, 156, 0.5) !important;
}

.spv-btn-next {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 24px !important;
  box-shadow: 0 4px 14px rgba(0, 100, 148, 0.4) !important;
  transition: all 0.3s ease !important;
}
.spv-btn-next:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 22px rgba(0, 100, 148, 0.6) !important;
}

/* Responsive */
@media (max-width: 960px) {
  .spv-hero-header {
    padding: 20px;
  }
  .spv-hero-title {
    font-size: 1.3rem;
  }
  .spv-steps-bar {
    padding: 16px;
  }
}
</style>
